import { DatePipe, TitleCasePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FetchPolicy } from 'apollo-client';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Assessment, IntakeForm, ModelSortDirection, Patient } from 'src/API';
import { AssessmentService } from 'src/app/core/api/assessment.service';
import { ClinicService } from 'src/app/core/api/clinic.service';
import { compareDateStrings, parseDateToString } from 'src/app/core/api/date.util';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { SideNavActionsService } from 'src/app/core/api/sidenav-actions.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { FilterGroup, MatchType } from 'src/app/filter/filter.model';
import { FilterService } from 'src/app/filter/filter.service';
import { IntakeFormService } from 'src/app/intake-form/intake-form.service';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { QuestionnaireScorePipe } from 'src/app/shared/shared-pipes/questionnaire-score.pipe';
import { QuestionnaireRiskPipe } from '../../shared/shared-pipes/questionnaire-risk.pipe';
import { FilterType } from './../../../API';
@Component({
  selector: 'csi-widgets-view',
  templateUrl: './widgets-view.component.html',
  styleUrls: ['./widgets-view.component.scss'],
  providers: [DatePipe, QuestionnaireScorePipe, QuestionnaireRiskPipe]
})
export class WidgetsViewComponent implements OnInit {
  cards: any[] = [];
  isLoading = true;
  intakeForms: IntakeForm[];
  patients: Partial<Patient>[] = [];
  staff: any[] = [];
  assessments: any[] = [];
  todaysTreatmentAssessments: any[] = [];
  recentlyCompletedQuestionnaires: any[] = [];
  questionnairesPendingCompletion: any[] = [];

  private assessmentBaseline: Assessment[] = [];
  private liveAssessments: Assessment[] = [];
  private patientBaseline: Partial<Patient>[] = [];
  private livePatients: Partial<Patient>[] = [];

  private refreshingCardIds: { [cardId: string]: boolean } = {};
  private cardLoaders: { [cardId: string]: (fetchPolicy?: FetchPolicy) => Promise<void> };

  private readonly cardTemplates = [
    { id: 'completed', title: 'Recently Completed Questionnaires', colour: 'blue' },
    { id: 'pending', title: 'Questionnaires Pending Completion', colour: 'blue' },
    { id: 'assessments', title: 'Recent Assessments', colour: 'orange' },
    { id: 'patients', title: 'Recent Patients', colour: 'green' },
    { id: 'intake', title: 'Latest Intake Forms', colour: 'purple' },
    { id: 'staff', title: 'Clinic Staff', colour: 'red' }
  ];

  constructor(
    private clinicSetupService: ClinicSetupService,
    private intakeFormService: IntakeFormService,
    public router: Router,
    private clinicService: ClinicService,
    private appStateService: AppStateService,
    public questionnaireRequestService: QuestionnaireRequestService,
    public sideNavActionsService: SideNavActionsService,
    private assessmentService: AssessmentService,
    public filterService: FilterService,
    private datePipe: DatePipe,
    public questionnaireScorePipe: QuestionnaireScorePipe,
    public questionnaireRiskPipe: QuestionnaireRiskPipe,
    private titleCasePipe: TitleCasePipe
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    this.cards = this.cardTemplates;
    this.cardLoaders = {
      staff: fetchPolicy => this.getStaff(fetchPolicy),
      intake: fetchPolicy => this.getIntakeForms(fetchPolicy),
      patients: () => this.getPatients(),
      assessments: fetchPolicy => this.getAssessments(fetchPolicy),
      pending: () => this.getQuestionnairesPendingCompletion(),
      completed: () => this.getRecentlyCompletedQuestionnaires()
    };

    await Promise.all(
      Object.keys(this.cardLoaders).map(cardId => this.loadCard(cardId))
    );

    this.subscribeToLiveUpdates();
    this.cards = this.generateCards();
    this.isLoading = false;
  }

  ngOnDestroy() {}

  private subscribeToLiveUpdates() {
    this.appStateService.assessments$.pipe(untilDestroyed(this)).subscribe(assessments => {
      this.liveAssessments = (assessments || []).filter(
        assessment =>
          !!assessment &&
          !!assessment.patient &&
          assessment.assessmentClinicId === this.clinicSetupService.clinicId
      );
      this.recomputeAssessmentViews();
      this.cards = this.generateCards();
    });

    this.appStateService.patients$.pipe(untilDestroyed(this)).subscribe(patients => {
      this.livePatients = (patients || []).filter(patient => !!patient && !!patient.id);
      this.recomputePatientViews();
      this.cards = this.generateCards();
    });
  }

  private async loadCard(cardId: string, fetchPolicy?: FetchPolicy): Promise<void> {
    try {
      await this.cardLoaders[cardId](fetchPolicy);
    } catch (error) {
      console.error('Failed to load dashboard widget: ' + cardId, error);
    }
  }

  private async refreshCard(cardId: string): Promise<void> {
    if (!this.cardLoaders[cardId] || this.refreshingCardIds[cardId]) {
      return;
    }

    this.refreshingCardIds[cardId] = true;
    this.cards = this.generateCards();
    try {
      await this.loadCard(cardId, 'network-only');
    } finally {
      this.refreshingCardIds[cardId] = false;
      this.cards = this.generateCards();
    }
  }

  trackCardById(index: number, card: any) {
    return card.id;
  }

  private mergeById<T>(...lists: T[][]): T[] {
    const byId: { [id: string]: T } = {};
    lists.forEach(list =>
      (list || []).forEach(item => {
        if (item && item['id']) {
          byId[item['id']] = item;
        }
      })
    );
    return Object.keys(byId).map(id => byId[id]);
  }

  private async getIntakeForms(fetchPolicy: FetchPolicy = 'cache-first'): Promise<void> {
    const data = await this.intakeFormService
      .getIntakeForms(this.clinicSetupService.clinicId, fetchPolicy)
      .toPromise();
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    this.intakeForms = data.reduce((acc, intakeForm) => {
      if (new Date(intakeForm.createdAt) < monthAgo) return acc;
      let response =
        typeof intakeForm.response === 'string'
          ? safeJsonParse(intakeForm.response)
          : intakeForm.response;

      if (response['questionnaire'] && response['questionnaire'].response) {
        response = {
          ...response,
          scores: response['questionnaire'].response.scores
        };
        intakeForm = {
          ...intakeForm,
          response
        };
      } else {
        const scores =
          typeof intakeForm.response === 'string'
            ? safeJsonParse(response['scores'])
            : safeJsonParse(response['scores']);
        const newResponse = { ...response, scores };
        intakeForm = { ...intakeForm, response: newResponse };
      }

      // Checks for legacy forms and transforms them into the new format
      if (response['patientInformation']) {
        const patientInformation = response['patientInformation'];
        const answers = { patientInformation };
        const newResponse = { ...response, answers };
        intakeForm = { ...intakeForm, response: newResponse };
      }

      // One instance of this, added for compatibility
      if (response['patientDemographics']) {
        const newResponse = {
          ...response,
          answers: { patientInformation: response['patientDemographics'] }
        };
        intakeForm = { ...intakeForm, response: newResponse };
      }

      acc.push(intakeForm);
      return acc;
    }, []);
  }

  private capitalizeName(name: string): string {
    if (!name) {
      return '';
    }
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  }

  private async getPatients(): Promise<void> {
    const patients = await this.filterService
      .queryFilterData(FilterType.patient, null)
      .pipe(untilDestroyed(this))
      .toPromise();

    this.patientBaseline = patients || [];
    this.recomputePatientViews();
  }

  private recomputePatientViews(): void {
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    this.patients = this.mergeById(this.patientBaseline, this.livePatients)
      .filter(patient => new Date(patient.updatedAt) >= monthAgo)
      .map(patient => ({
        ...patient,
        firstName: this.capitalizeName(patient.firstName),
        lastName: this.capitalizeName(patient.lastName)
      }))
      .sort((a, b) => compareDateStrings(b.updatedAt, a.updatedAt));
  }

  private async getQuestionnairesPendingCompletion(): Promise<void> {
    const filterValue =
      this.clinicSetupService.clinicSetupFormGroup.controls.questionnaireExpiry.value || 30;

    try {
      const questionnaires = await this.filterService
        .queryFilterData(FilterType.questionnaireRequest, [
          this.generateFilterGroup('createdAt', filterValue)
        ])
        .pipe(untilDestroyed(this))
        .toPromise();

      this.questionnairesPendingCompletion = (questionnaires || [])
        .filter(q => !q.completedAt)
        .sort((a, b) => compareDateStrings(b.createdAt, a.createdAt));
    } catch (error) {
      console.error(error);
      this.questionnairesPendingCompletion = [];
    }
  }

  private async getRecentlyCompletedQuestionnaires(): Promise<void> {
    try {
      const questionnaires = (await this.filterService
        .queryFilterData(FilterType.questionnaireRequest, [
          this.generateFilterGroup('completedAt', 30)
        ])
        .pipe(untilDestroyed(this))
        .toPromise()) || [];

      this.recentlyCompletedQuestionnaires = questionnaires.sort((a, b) =>
        compareDateStrings(b.completedAt, a.completedAt)
      );
    } catch (error) {
      console.error(error);
      this.recentlyCompletedQuestionnaires = [];
    }
  }

  private generateFilterGroup(field: string, timeFrameInDays: number): FilterGroup {
    const dateToday = new Date();

    // Calculate the date for two weeks ago
    const startDate = new Date();
    startDate.setDate(dateToday.getDate() - timeFrameInDays * 2);

    return {
      type: MatchType.and,
      filters: [
        {
          field,
          operator: 'greaterThanOrEqualTo',
          searchValue: startDate.toISOString()
        },
        {
          field,
          operator: 'lessThanOrEqualTo',
          searchValue: dateToday.toISOString()
        }
      ]
    };
  }

  private async getAssessments(fetchPolicy: FetchPolicy = 'cache-first'): Promise<void> {
    // createdAt is an AWSDateTime compared as a raw string by the DynamoDB BETWEEN in the
    // resolver, so the bounds have to be full ISO instants. Date-only bounds sort below every
    // timestamp on the same day and silently drop it.
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    const windowStart = new Date();
    windowStart.setDate(windowStart.getDate() - 90);
    windowStart.setHours(0, 0, 0, 0);

    try {
      const [recent, todays] = await Promise.all([
        this.assessmentService
          .listAssessmentsByDateRange([windowStart.toISOString(), endOfToday.toISOString()], {
            limit: 25,
            sortDirection: ModelSortDirection.DESC,
            fetchPolicy
          })
          .pipe(untilDestroyed(this))
          .toPromise(),
        this.assessmentService
          .listAssessmentsByDateRange([startOfToday.toISOString(), endOfToday.toISOString()], {
            sortDirection: ModelSortDirection.DESC,
            fetchPolicy
          })
          .pipe(untilDestroyed(this))
          .toPromise()
      ]);

      this.assessmentBaseline = this.mergeById(recent, todays);
      this.recomputeAssessmentViews();
    } catch (error) {
      this.assessmentBaseline = [];
      this.recomputeAssessmentViews();
    }
  }

  private recomputeAssessmentViews(): void {
    const todayString = parseDateToString(new Date());

    const mappedAssessments = this.mergeById(this.assessmentBaseline, this.liveAssessments).map(
      assessment => {
        const parsedBody = safeJsonParse(assessment.body);
        const method =
          parsedBody && parsedBody.dryEyeForm && parsedBody.dryEyeForm.assessmentMethod;

        const friendlyType = this.assessmentService.encounterMethodFriendlyName[method] || method;

        return { ...assessment, body: parsedBody, type: friendlyType };
      }
    );

    this.assessments = mappedAssessments
      .slice()
      .sort((a, b) => compareDateStrings(b.createdAt, a.createdAt));

    this.todaysTreatmentAssessments = mappedAssessments
      .filter(
        assessment =>
          parseDateToString(new Date(assessment.createdAt)) === todayString &&
          this.assessmentHasTreatment(assessment.body)
      )
      .sort((a, b) => compareDateStrings(b.createdAt, a.createdAt));
  }

  private assessmentHasTreatment(body: any): boolean {
    const assessmentForm = body && body.assessmentForm;
    const dryEyeForm = body && body.dryEyeForm;

    const hasItems = (items: any[]) => Array.isArray(items) && items.filter(Boolean).length > 0;
    const hasText = (text: string) => typeof text === 'string' && text.trim().length > 0;

    return (
      !!(
        assessmentForm &&
        (hasText(assessmentForm.recommendedPlanByDoctor) ||
          hasItems(assessmentForm.recommendedExercises) ||
          hasItems(assessmentForm.recommendedEyeDrops) ||
          hasItems(assessmentForm.recommendedRxEyeDrops) ||
          hasItems(assessmentForm.recommendedProcedures) ||
          hasText(assessmentForm.treatmentPerformed))
      ) || !!(dryEyeForm && hasText(dryEyeForm.treatmentsPlan))
    );
  }

  private async getStaff(fetchPolicy: FetchPolicy = 'cache-first'): Promise<void> {
    const data = await this.clinicService
      .getClinicStaff(this.clinicSetupService.clinicId, fetchPolicy)
      .toPromise();
    const order = { 'Doctor / Owner': 1, Doctor: 2, Tech: 3, Receptionist: 4 };
    this.staff = data
      .filter(staff => !staff.deactivated)
      .map(staff => ({
        ...staff,
        staffType: staff.staffType || 'Doctor / Owner'
      }))
      .sort((a, b) => (order[a.staffType] || 5) - (order[b.staffType] || 5));
  }

  private concatName = val =>
    this.titleCasePipe.transform(val.firstName.split(' ')[0] + ' ' + val.lastName);

  getCachedPatientName(id: string) {
    const cachedPatient = this.filterService.filterTypeCachedFilterMap['patient'].data[id];

    if (!cachedPatient) {
      return '';
    }

    let patient = {
      firstName: this.filterService.filterTypeCachedFilterMap['patient'].data[id].firstName,
      lastName: this.filterService.filterTypeCachedFilterMap['patient'].data[id].lastName
    };
    return this.concatName(patient);
  }

  private generateCards(): any[] {
    return this.buildCards().map(card => this.withRefreshButton(card));
  }

  private withRefreshButton(card: any): any {
    const refreshing = !!this.refreshingCardIds[card.id];
    return {
      ...card,
      buttons: [
        ...(card.buttons || []),
        {
          icon: 'refresh',
          tooltip: 'Refresh',
          action: () => this.refreshCard(card.id),
          disabled: refreshing,
          spinning: refreshing
        }
      ]
    };
  }

  private buildCards(): any[] {
    return this.cardTemplates.map(template => {
      switch (template.id) {
        case 'completed':
          return {
            ...template,
            content: [
              {
                heading: 'Name',
                transform: (val: any) =>
                  this.getCachedPatientName(val.questionnaireRequestPatientId)
              },
              {
                heading: 'Type',
                transform: (val: any) =>
                  this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap[val.type]
              },
              {
                heading: 'Completed On',
                transform: (val: any) => this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
              }
            ],
            clickable: {
              action: (val: Patient) =>
                this.router.navigateByUrl('patients/' + val['questionnaireRequestPatientId'])
            },
            data: this.recentlyCompletedQuestionnaires
          };
        case 'pending':
          return {
            ...template,
            content: [
              {
                heading: 'Name',
                transform: (val: any) =>
                  this.getCachedPatientName(val.questionnaireRequestPatientId)
              },
              {
                heading: 'Type',
                transform: (val: any) =>
                  this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap[val.type]
              },
              {
                heading: 'Date Sent',
                transform: (val: any) => this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
              }
            ],
            clickable: {
              action: (val: Patient) =>
                this.router.navigateByUrl('patients/' + val['questionnaireRequestPatientId'])
            },
            data: this.questionnairesPendingCompletion
          };
        case 'patients':
          return {
            ...template,
            expandedTitle: 'Patients for the last month',
            content: [
              {
                heading: 'Name',
                transform: (val: IntakeForm) => this.concatName(val)
              },
              {
                heading: 'Updated On',
                transform: (val: Patient) => this.datePipe.transform(val.updatedAt, 'MMM dd, yyyy')
              }
            ],
            expandedContent: [
              {
                heading: 'First Name',
                transform: (val: IntakeForm) => val['firstName']
              },
              {
                heading: 'Last Name',
                transform: (val: IntakeForm) => val['lastName']
              },
              {
                heading: 'Last Updated',
                transform: (val: Patient) =>
                  this.datePipe.transform(val.updatedAt, 'MMM dd, yyyy HH:mm')
              }
            ],
            clickable: {
              action: (val: Patient) => this.router.navigateByUrl('patients/' + val.id)
            },
            buttons: [
              {
                icon: 'open_in_new',
                tooltip: 'Open Patient Management',
                action: () => this.router.navigateByUrl('patients')
              }
            ],
            data: this.patients
          };
        case 'assessments': {
          const assessmentClickable = {
            action: (val: Assessment) =>
              this.router.navigateByUrl(
                'patients/' + val.patient.id + '/(encounters:' + val.id + ')'
              )
          };
          return {
            ...template,
            tabs: [
              {
                id: 'recent',
                label: 'Recent',
                emptyMessage: 'No recent assessments',
                content: [
                  {
                    heading: 'Name',
                    transform: (val: any) => this.concatName(val.patient)
                  },
                  {
                    heading: 'Type',
                    item: 'type'
                  },
                  {
                    heading: 'Completed On',
                    transform: (val: any) => this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
                  }
                ],
                clickable: assessmentClickable,
                data: this.assessments
              },
              {
                id: 'todaysTreatments',
                label: 'Today\'s Treatments',
                emptyMessage: 'No today\'s treatments',
                content: [
                  {
                    heading: 'Name',
                    transform: (val: any) => this.concatName(val.patient)
                  },
                  {
                    heading: 'Assessment Date',
                    transform: (val: any) => this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
                  }
                ],
                clickable: assessmentClickable,
                data: this.todaysTreatmentAssessments
              }
            ]
          };
        }
        case 'intake':
          return {
            ...template,
            expandedTitle: 'Intake Forms for the last month',
            content: [
              {
                heading: 'Name',
                transform: (val: IntakeForm) =>
                  this.concatName(val.response['answers'].patientInformation)
              },
              {
                item: 'createdAt',
                heading: 'Completed On',
                transform: (val: IntakeForm) =>
                  this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
              },
              {
                heading: 'Score',
                transform: (val: any) =>
                  this.questionnaireRiskPipe.transform(this.questionnaireScorePipe.transform(val))
              }
            ],
            expandedContent: [
              {
                heading: 'First Name',
                transform: (val: IntakeForm) => val.response['answers'].patientInformation.firstName
              },
              {
                heading: 'Last Name',
                transform: (val: IntakeForm) => val.response['answers'].patientInformation.lastName
              },
              {
                item: 'createdAt',
                heading: 'Completed On',
                transform: (val: IntakeForm) =>
                  this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
              },
              {
                heading: 'Score',
                transform: (val: any) =>
                  this.questionnaireRiskPipe.transform(this.questionnaireScorePipe.transform(val))
              }
            ],
            buttons: [
              {
                icon: 'qr_code',
                tooltip: 'Intake Form QR Code',
                action: () =>
                  this.sideNavActionsService.openClinicSetupModal({
                    selectedTabLabel: 'Intake Form'
                  })
              },
              {
                icon: 'open_in_new',
                tooltip: 'Open Intake Forms',
                action: () => this.router.navigateByUrl('intake-forms')
              }
            ],
            clickable: {
              action: (val: IntakeForm) => this.router.navigateByUrl('intake-forms/' + val.id)
            },
            data: this.intakeForms
          };
        case 'staff':
          return {
            ...template,
            content: [
              {
                heading: 'Name',
                transform: (val: IntakeForm) => this.concatName(val)
              },
              { item: 'staffType', heading: 'Position' }
            ],
            buttons: [
              {
                icon: 'open_in_new',
                tooltip: 'Open Staff Management',
                action: () =>
                  this.sideNavActionsService.openClinicSetupModal({
                    selectedTabLabel: 'Clinic Staff'
                  })
              }
            ],
            data: this.staff
          };
        default:
          return template;
      }
    });
  }
}
